"use client";

import { logSDUIEvent } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/EventLogging/logSDUIEvent";
import {
  ActionLoggingEventParams,
  SectionLoggingEventParams,
} from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/types";
import { PAGE_QUERY_PARAM_KEY } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/consts";
import { FilterablePaginatedSectionContext } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/FilterablePaginatedSectionContext";
import { useSDUIFilterablePaginatedSectionState } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/Filters/useSDUIFilterablePaginatedSectionState";
import { DeferredSDUIFilterablePaginatedSectionNextPagePrefetcherRSC } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/Pagination/DeferredSDUIFilterablePaginatedSectionNextPagePrefetcherRSC";
import { SDUIFilterablePaginatedSectionNextPagePrefetcher } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/Pagination/SDUIFilterablePaginatedSectionNextPagePrefetcher";
import { trackCategoriesPaginationClick } from "@faire/web-api--source/events/categories/click/pagination";
import { trackSearchPaginationActionCategoryClick } from "@faire/web-api--source/events/search/actionCategoryClick/pagination";
import { DisplayPageType } from "@faire/web-api--source/indigofair/data/DisplayPageType";
import { IFilterablePaginatedSection } from "@faire/web-api--source/indigofair/data/IFilterablePaginatedSection";
import { IPaginationData } from "@faire/web-api--source/indigofair/data/IPaginationData";
import { getCurrentPath } from "@faire/web/common/Path";
import { Pagination } from "@faire/web/slate/Pagination";
import { WhenVisibilityIsImminent } from "@faire/web/ui/VisibilitySensor/WhenVisibilityIsImminent";
import React, { useCallback, useContext } from "react";

export const SDUIFilterablePaginatedSectionPaginationRenderer: React.FC<{
  paginationData: IPaginationData | undefined;
  sectionLoggingData: SectionLoggingEventParams;
  filterablePaginatedSection: IFilterablePaginatedSection | undefined;
  isRSC?: boolean;
}> = ({
  paginationData,
  sectionLoggingData,
  filterablePaginatedSection,
  isRSC,
}) => {
  const { setOneIndexedPageNumber } =
    useSDUIFilterablePaginatedSectionState(!!isRSC);

  const handlePaginationLink = useCallback((pageNumber: number): string => {
    const currentFilterPath = getCurrentPath().delete(PAGE_QUERY_PARAM_KEY);

    if (pageNumber === 1) {
      return currentFilterPath.toString();
    }
    return currentFilterPath.append({ page: pageNumber.toString() }).toString();
  }, []);

  const {
    scrollToTopFunc,
    setIsScrollingToTop,
    isTransitioningFunction,
    setIsTransitioning,
  } = useContext(FilterablePaginatedSectionContext);

  const handlePageChange = useCallback(
    (value: number) => {
      if (isRSC && value !== paginationData?.page_number) {
        isTransitioningFunction?.(true);
        setIsTransitioning?.(true);
        setTimeout(() => {
          setOneIndexedPageNumber(() => {
            return value;
          });
        }, 0);
      } else {
        setOneIndexedPageNumber(() => {
          return value;
        });
      }
    },
    [
      isRSC,
      setOneIndexedPageNumber,
      isTransitioningFunction,
      setIsTransitioning,
    ]
  );

  const shouldShowPagination =
    paginationData && (paginationData.page_count ?? 0) > 1;

  if (!shouldShowPagination) {
    return null;
  }

  return (
    <>
      {sectionLoggingData.view_display_page === DisplayPageType.SEARCH_PAGE ? (
        <DeferredSDUIFilterablePaginatedSectionNextPagePrefetcherRSC
          handlePaginationLink={handlePaginationLink}
        />
      ) : null}
      <WhenVisibilityIsImminent>
        <div
          className="border-core-border-muted mt-6 border-t border-solid px-0 pt-6 pb-2 md:mx-0 md:mt-10 md:pb-0"
          data-test-id="sdui-fps-pagination"
        >
          {sectionLoggingData.view_display_page !==
          DisplayPageType.SEARCH_PAGE ? (
            <SDUIFilterablePaginatedSectionNextPagePrefetcher
              filterablePaginatedSection={filterablePaginatedSection}
              paginationData={paginationData}
              handlePaginationLink={handlePaginationLink}
              isRSC={isRSC}
            />
          ) : null}
          <Pagination
            paginationData={paginationData}
            pageToLink={handlePaginationLink}
            onChangePage={(page, e) => {
              if (isRSC) {
                e.preventDefault();
              }
              logSDUIEvent(
                "CLICK",
                ActionLoggingEventParams.build({
                  ...sectionLoggingData,
                  action_content: page.toString(),
                  action_instance_identifier: "FETCH_PAGE",
                })
              );
              if (
                sectionLoggingData.view_display_page ===
                DisplayPageType.CATEGORY_PAGE
              ) {
                trackCategoriesPaginationClick(
                  sectionLoggingData.view_instance_identifier ?? "",
                  page.toString()
                );
              }
              if (
                sectionLoggingData.view_display_page ===
                DisplayPageType.SEARCH_PAGE
              ) {
                trackSearchPaginationActionCategoryClick(
                  sectionLoggingData.view_instance_identifier ?? "",
                  page.toString()
                );
              }
              scrollToTopFunc?.({ toTop: page === 1 });
              if (!isRSC) {
                setIsScrollingToTop?.(true);
              }
              handlePageChange(page);
            }}
          />
        </div>
      </WhenVisibilityIsImminent>
    </>
  );
};
